import { useTranslation } from 'react-i18next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@e2n/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faEllipsisV,
  faFileLines,
  faTrashCan,
} from '@fortawesome/pro-regular-svg-icons';
import { docsUrl } from '../config.ts';
import { useState } from 'react';
import { LoeschenDialog } from './LoeschenDialog.tsx';
import { useNavigate } from 'react-router-dom';

type Darstellung = 'menue' | 'leiste';

type ZugangActionsProps = {
  uidZugang: string;
  editierbar: boolean;
  darstellung: Darstellung;
};

export function ZugangActions({
  uidZugang,
  editierbar,
  darstellung,
}: ZugangActionsProps) {
  const [loeschenOffen, setLoeschenOffen] = useState(false);

  return (
    <>
      {darstellung === 'menue' ? (
        <AktionenMenue
          uidZugang={uidZugang}
          editierbar={editierbar}
          onLoeschen={() => setLoeschenOffen(true)}
        />
      ) : (
        <AktionenLeiste
          uidZugang={uidZugang}
          editierbar={editierbar}
          onLoeschen={() => setLoeschenOffen(true)}
        />
      )}
      <LoeschenDialog
        open={loeschenOffen}
        onOpenChange={setLoeschenOffen}
        uidZugang={uidZugang}
      />
    </>
  );
}

type AktionenProps = {
  uidZugang: string;
  editierbar: boolean;
  onLoeschen: () => void;
};

function AktionenMenue({ uidZugang, editierbar, onLoeschen }: AktionenProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <FontAwesomeIcon icon={faEllipsisV} className="text-primary-blue-600" />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem asChild>
          <a
            href={docsUrl(uidZugang)}
            target="_blank"
            rel="noreferrer"
            className="flex gap-2">
            <FontAwesomeIcon icon={faFileLines} />
            <span>{t('domain.e2nApi.zugaenge.aktionen.dokumentation')}</span>
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!editierbar}
          onClick={() => navigate(`zugang/${uidZugang}/bearbeiten`)}
          className="flex gap-2">
          <FontAwesomeIcon icon={faEdit} />
          <span>{t('domain.e2nApi.zugaenge.aktionen.bearbeiten')}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onLoeschen}
          className="flex gap-2 text-primary-red-400">
          <FontAwesomeIcon icon={faTrashCan} />
          <span>{t('domain.e2nApi.zugaenge.aktionen.loeschen')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AktionenLeiste({ uidZugang, editierbar, onLoeschen }: AktionenProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="flex gap-2">
      <Button
        variant="icon"
        size="icon"
        asChild
        className="text-primary-blue-600">
        <a
          href={docsUrl(uidZugang)}
          target="_blank"
          rel="noreferrer"
          aria-label={t('domain.e2nApi.zugaenge.aktionen.dokumentation')}>
          <FontAwesomeIcon icon={faFileLines} />
        </a>
      </Button>
      <Button
        variant="icon"
        size="icon"
        className="text-primary-blue-600"
        disabled={!editierbar}
        onClick={() => navigate(`zugang/${uidZugang}/bearbeiten`)}
        aria-label={t('domain.e2nApi.zugaenge.aktionen.bearbeiten')}>
        <FontAwesomeIcon icon={faEdit} />
      </Button>
      <Button
        variant="icon"
        size="icon"
        className="text-primary-red-400"
        onClick={onLoeschen}
        aria-label={t('domain.e2nApi.zugaenge.aktionen.loeschen')}>
        <FontAwesomeIcon icon={faTrashCan} />
      </Button>
    </div>
  );
}
